import * as THREE from "three";

import { Utils } from "../../utils/utils";
import { Loader } from "../assets/loader";
import Renderer from "../../renderer/renderer";
import CameraService from "../../services/camera/camera.service";
import PhysicsService from "../../services/physics/physics.service"
import SoundService from "../../services/sound/sound.service";

import AppData from "../../app.data";
import { SpaceEnvironmentManager } from "./managers/environment.manager";
import { SpaceAssetManager } from "./managers/assets.manager";
import { SpaceProceduralManager } from "./managers/procedural.manager";
import { europeanaCountries, shouldShowPseudoMoMCountryLabel } from "../../../../types/europeanaCountries";
import { getMoodCameraHeight } from "../../../modals/render.settings.camera";

const appData = new AppData()

/**
 * Handles loading and management of 3D spaces/environments
 * Manages space initialization, loading of assets, materials, lighting, and procedural space generation
 * Coordinates with physics, camera, and other services for complete space setup
 */
class SpaceLoader {
  /** Asset loader instance */
  loader: Loader;
  /** Renderer instance */
  renderer: Renderer;
  /** Physics management service */
  physicsService: PhysicsService;
  /** Camera management service */
  cameraService: CameraService;
  /** Utility functions */
  utils: Utils;

  environmentManager: SpaceEnvironmentManager;
  assetsManager: SpaceAssetManager;
  proceduralManager: SpaceProceduralManager;

  /**
   * Initializes the space loader with required services
   * @param loader Asset loader instance
   * @param renderer Renderer instance
   * @param physicsService Physics service for collision handling
   * @param cameraService Camera service for view management
   */
  constructor(loader, renderer, physicsService, cameraService) {
    this.loader = loader;
    this.renderer = renderer;
    this.physicsService = physicsService;
    this.cameraService = cameraService;
    this.utils = new Utils();
    appData.spaceScene = new THREE.Scene();

    this.environmentManager = new SpaceEnvironmentManager(renderer, loader, this.utils);
    this.assetsManager = new SpaceAssetManager(loader, renderer);
    this.proceduralManager = new SpaceProceduralManager(this.utils, cameraService, renderer);
  }

  /**
   * Main method to load and setup the space
   * Initializes space, loads assets, and handles results
   */
  public async loadSpace() {
    this.initializeSpace();
    appData.physicsService = this.physicsService;

    const promises = this.assembleLoadingPromises();

    try {
      const results = await Promise.all(promises);
      this.handleLoadedResults(results);
      appData.physicsService = this.physicsService
    } catch (error) {
      console.error("Error loading space assets:", error);
    }
  }

  public async changeMood(mood: any, local?: any) {
    try {
      await this.environmentManager.changeMood(mood, local);
    } catch (error) {
      console.error("Error changing mood:", error);
    }
  }

  /**
   * Cleans up background and environment resources
   */
  public dispose = () => {
    this.utils.disposeOfChildren(appData.spaceScene);
    this.environmentManager.dispose();
    this.physicsService.dispose();
  };

  /**
   * Completely destroys the space and cleans up all resources
   */
  public destroy = () => {
    this.dispose();
    // Managers handle their own cleanup or are garbage collected
    this.loader = null as any;
    this.renderer = null as any;
    this.physicsService = null as any;
    this.cameraService = null as any;
    this.utils = null as any;
  };

  /**
   * Initializes the space environment
   * Sets up fog and lighting based on current mood
   */
  private initializeSpace(): void {
    if (!appData.spaceScene) {
      appData.spaceScene = new THREE.Scene();
    }
    const moods = appData.spaceData?.data?.moods || [];
    const requestedMoodId = Number.isInteger(appData.galleryData?.data?.mood) ? appData.galleryData.data.mood : 0;
    this.environmentManager.mood = moods[requestedMoodId] || moods[0] || {
      published: false,
      name: "Fallback Mood",
      description: "",
      thumbnailUrl: "",
      fog: { enabled: false, near: 1, far: 100, color: "#ffffff", applyFogToArtworks: false },
      lights: [],
      sky: { hdriUrl: "", skyImageUrl: "", skyModelName: "sky", hdriVisible: true, hdrIntensity: 1 },
      materialOverrides: [],
      renderSettings: { toneMapping: 1, toneMappingExposure: 1, postProcessing: [{ type: "render" }], artworkMapIntensity: 1 },
      lightMap: { selectedMeshes: [], lightMapUrl: "" }
    };
    appData.runtimeCameraHeight = getMoodCameraHeight(this.environmentManager.mood);

    // remove existing space
    this.utils.disposeOfChildren(appData.spaceScene);

    // set fog
    this.environmentManager.setFog();

    // initialize holders
    this.environmentManager.initializeHolders();

    appData.artworksScene = new THREE.Scene()
    appData.artworksScene.name = "artworksScene"
    appData.spaceScene.add(appData.artworksScene)

    //set lights
    this.utils.createLights(this.environmentManager.mood.lights, appData);
  }

  /**
   * Creates promises for loading all required space assets
   * Includes models, HDR maps, textures, and clones
   * @returns Array of loading promises
   */
  private assembleLoadingPromises(): Promise<any>[] {
    return this.assetsManager.assembleLoadingPromises(this.environmentManager.mood);
  }

  /**
   * Processes loaded assets and sets up the space
   * Handles background, environment, clones, materials, and navigation
   * @param results Array of loaded assets
   */
  private handleLoadedResults(results: any[]): void {
    const [gltfScene, hdri, sky, lightMap, ...remaining] = results;

    const clonesCount = this.assetsManager.clonesCount;
    const clones = remaining.slice(0, clonesCount);
    const materialOverrides = remaining.slice(clonesCount);

    gltfScene.name = "sceneHolder"

    appData.spaceScene.add(gltfScene);

    this.environmentManager.handleLightMap(this.environmentManager.mood, lightMap)

    this.assetsManager.handleClone(clones)

    this.assetsManager.handleProps(appData.spaceData?.data?.props || [])

    if (sky) this.environmentManager.handleSkyImage(this.environmentManager.mood, sky)
    if (hdri) this.environmentManager.handleHDR(this.environmentManager.mood, hdri)

    this.assetsManager.overrideMaterials(this.environmentManager.mood, materialOverrides)

    this.physicsService.handleNavMesh(gltfScene);

    if (appData.galleryData.data.spaceProceduralParameters) {
      this.proceduralManager.handleProceduralSpace()
    }

    if (appData.spaceData.subtype === "pseudoMoM") {
      this.handlePseudoMoMLights()
      this.addCountryCodesToEuropeanaSlots()
    }
  }


  private attachPseudoMoMLightMarker(light: THREE.PointLight, color: number): void {
    const radius = 0.845;
    const geometry = new THREE.CylinderGeometry(radius, radius, 0.03, 64);
    const material = new THREE.MeshPhysicalMaterial({
      color: color,
      roughness: 0.6,
      metalness: 0,
      transparent: true,
      opacity: 0.7,
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.y = -1.7898 - 2;
    light.add(mesh);
  }

  private handlePseudoMoMLights(): void {
    const lightDistance = 100
    const lightIntensity = 4
    const lightDecay = 0;
    const lightColor = 0xffB64E;
    const light1 = new THREE.PointLight(lightColor, lightIntensity, lightDecay);
    light1.userData.pseudoMoMBaseColor = lightColor;
    light1.position.set(0, -50, 0);
    light1.distance = lightDistance;
    this.attachPseudoMoMLightMarker(light1, 0xFF00FF);
    appData.spaceScene.add(light1);


    const light2 = new THREE.PointLight(lightColor, lightIntensity, lightDecay);
    light2.userData.pseudoMoMBaseColor = lightColor;
    light2.position.set(0, -50, 0);
    light2.distance = lightDistance;
    this.attachPseudoMoMLightMarker(light2, 0xFFA500);
    appData.spaceScene.add(light2);

    const light3 = new THREE.PointLight(lightColor, lightIntensity, lightDecay);
    light3.userData.pseudoMoMBaseColor = lightColor;
    light3.position.set(0, -50, 0);
    light3.distance = lightDistance;
    this.attachPseudoMoMLightMarker(light3, 0x89cff0);
    appData.spaceScene.add(light3);

    const light4 = new THREE.PointLight(lightColor, lightIntensity, lightDecay);
    light4.userData.pseudoMoMBaseColor = lightColor;
    light4.position.set(0, -50, 0);
    light4.distance = lightDistance;
    this.attachPseudoMoMLightMarker(light4, 0xfFFFF00);
    appData.spaceScene.add(light4);

    appData.spaceData.pseudoMoMLights = [light1, light2, light3, light4];
  }

  private addCountryCodesToEuropeanaSlots(): void {
    const slots = appData.spaceData.data.slots;
    const countryCodes = europeanaCountries;
    for (let index = 0; index < countryCodes.length; index++) {
      const countryCode = countryCodes[index];
      if (!shouldShowPseudoMoMCountryLabel(countryCode)) continue;

      const canvas = document.createElement("canvas");
      canvas.width = 256;
      canvas.height = 256;
      const ctx = canvas.getContext("2d");
      if (!ctx) continue;


      ctx.fillStyle = "#000000";
      ctx.font = "700 68px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(countryCode, canvas.width / 2, canvas.height / 2);

      const texture = new THREE.CanvasTexture(canvas);
      texture.needsUpdate = true;

      // Use a sprite so marker size stays consistent on screen.
      const material = new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        sizeAttenuation: false
      });
      const sprite = new THREE.Sprite(material);
      const slot = slots[index]
      const position = slot.transforms.position
      sprite.position.set(position.x, 4.5, position.z);
      const spriteSize = 0.08
      sprite.scale.set(spriteSize, spriteSize, spriteSize);
      sprite.name = `country_sprite_${countryCode}`;
      sprite.userData.countryCode = countryCode;
      appData.spaceScene.add(sprite);
    }

  }
}

export default SpaceLoader;
